import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function IsolationPage(): Node {
  return html`
    <div>
      <h1>Isolation Modes</h1>
      <p>Control how remote microfrontends interact with the host DOM. Choose the right level of isolation for your use case.</p>

      <h2>Comparison</h2>
      <table>
        <tr><th>Mode</th><th>DOM Access</th><th>Style Leak</th><th>JS Scope</th><th>Performance</th><th>Use Case</th></tr>
        <tr><td><code>none</code></td><td>Full</td><td>Yes</td><td>Shared</td><td>Best</td><td>Trusted, same-team remotes</td></tr>
        <tr><td><code>shadow</code></td><td>Scoped</td><td>No</td><td>Shared</td><td>Good</td><td>Style isolation needed</td></tr>
        <tr><td><code>iframe</code></td><td>None</td><td>No</td><td>Isolated</td><td>Fair</td><td>Untrusted or legacy code</td></tr>
      </table>

      <h2>None (Default)</h2>
      <p>The remote component renders directly into the host DOM. No boundaries.</p>
      ${CodeBlock(`loadRemote({
  url: 'https://cdn.example.com/widget.js',
  isolation: 'none', // default
})`)}
      <ul>
        <li>Host CSS affects the remote.</li>
        <li>Remote can access host DOM via standard APIs.</li>
        <li>Best performance — no extra layers.</li>
      </ul>

      <h2>Shadow DOM</h2>
      <p>Renders the remote inside a Shadow DOM boundary. Styles are encapsulated.</p>
      ${CodeBlock(`loadRemote({
  url: 'https://cdn.example.com/widget.js',
  isolation: 'shadow',
})`)}
      <ul>
        <li>Host styles don't leak into the remote.</li>
        <li>Remote styles don't affect the host.</li>
        <li>JavaScript scope is still shared (same window).</li>
        <li>Good for design system isolation between teams.</li>
      </ul>

      <h2>Iframe</h2>
      <p>Full isolation via a sandboxed iframe. The remote runs in a separate browsing context.</p>
      ${CodeBlock(`loadRemote({
  url: 'https://cdn.example.com/widget.js',
  isolation: 'iframe',
})`)}
      <ul>
        <li>Complete DOM isolation.</li>
        <li>Separate JavaScript execution context.</li>
        <li>Communication via <code>postMessage</code> only.</li>
        <li>Higher memory overhead.</li>
        <li>Best for untrusted third-party code.</li>
      </ul>

      ${Callout('Use "shadow" for same-organization teams that need style isolation. Use "iframe" only for untrusted or legacy code that might pollute globals.', 'warn')}

      <h2>Choosing the Right Mode</h2>
      <table>
        <tr><th>Scenario</th><th>Recommended</th></tr>
        <tr><td>Internal widget, same design system</td><td><code>none</code></td></tr>
        <tr><td>Internal widget, different team/styles</td><td><code>shadow</code></td></tr>
        <tr><td>Third-party embed, untrusted code</td><td><code>iframe</code></td></tr>
        <tr><td>Legacy jQuery/Angular widget</td><td><code>iframe</code></td></tr>
      </table>
    </div>
  `;
}
